import Vue from 'vue';

const EXAM_TYPES = {
  practice: 'Practice Exam',
  course: 'Course Exam',
  track: 'Final Exam',
};

export default (store) => {
  store.registerModule('exams', {
    namespaced: true,
    state: () => ({
      exam: {},
      currentQuestionIdx: 0,
      answers: {},
      reviewQuestions: false,
      now: null,
      timer: null,
      questionsTimeSpent: { },
      examProcessing: false,
      leftDirection: false,
    }),
    getters: {
      currentQuestion(state) {
        const question = state.exam.questions[state.currentQuestionIdx];
        const c = state.exam.cases.find((s) => s.id === question.caseId);
        question.number = state.currentQuestionIdx + 1;

        const caseQuestions = state.exam.questions.filter((q) => q.caseId === question.caseId);

        const newExamType = state.exam.questions[0].order !== 0;

        return {
          ...question,
          case: c,
          firstForCase: !newExamType ? question.order % caseQuestions.length === 0 : question.order === 1,
          secondForCase: !newExamType ? question.order % caseQuestions.length === 1 : question.order === 2,
        };
      },
      progress(state) {
        return Math.round(((state.currentQuestionIdx + 1) / state.exam.questions.length) * 100);
      },
      answered(state) {
        return Object.values(state.answers).filter((a) => a.answer).length;
      },
      flagged(state) {
        return Object.values(state.answers).filter((a) => a.flag).length;
      },
      notAnswered(state) {
        return Object.values(state.answers).filter((a) => !a.answer).length;
      },
      correct(state) {
        return Object.values(state.answers).filter((a) => a.correct).length;
      },
      incorrect(state) {
        return Object.values(state.answers).filter((a) => !a.correct).length;
      },
      onFirstQuestion(state) {
        return state.currentQuestionIdx === 0;
      },
      onLastQuestion(state) {
        return state.currentQuestionIdx === state.exam.questions.length - 1;
      },
    },
    mutations: {
      replaceState(state, s) {
        Object.assign(state, s);
      },
      setExam(state, exam) {
        state.exam = {
          started: Date.now(),
          endTimestamp: Date.now() + exam.timeout * 60000,
          ...exam,
          typeDisplay: EXAM_TYPES[exam.type],
        };
        state.answers = exam.questions.reduce((p, c) => ({
          ...p,
          [c.id]: {
            answer: c.answer, flag: c.flag, correct: c.correct, time: 0,
          },
        }), {});
      },
      resetExam(state) {
        state.exam = {};
      },
      setExamCompleted(state) {
        Vue.set(state.exam, 'completed', new Date());
      },
      setLeftDirection(state, boolean) {
        state.leftDirection = boolean;
      },
      setCurrentQuestionIdx(state, question) {
        state.currentQuestionIdx = question;
      },
      setCurrentQuestionIdxById(state, questionId) {
        const idx = state.exam.questions.findIndex((q) => q.id === questionId);
        state.currentQuestionIdx = idx;
      },
      setAnswer(state, { question, answer }) {
        Vue.set(state.answers[question], 'answer', answer);
      },
      setAnswerCreated(state, { question }) {
        Vue.set(state.answers[question], 'created', Math.round(new Date().getTime() / 1000));
      },
      setFlag(state, { question, flag }) {
        Vue.set(state.answers[question], 'flag', flag);
      },
      setFlagCreated(state, { question }) {
        Vue.set(state.answers[question], 'flagCreated', Math.round(new Date().getTime() / 1000));
      },
      reviewQuestions(state, s) {
        state.reviewQuestions = typeof s !== 'undefined' ? s : !state.reviewQuestions;
      },
      setNow(state, d) {
        const now = d || Date.now();
        state.now = now;
      },
      setTimeSpent(state, { question, time }) {
        const prevTime = (state.answers[question].time || 0) + 1;
        Vue.set(state.answers[question], 'time', typeof time !== 'undefined' ? time : prevTime);
      },
      setTimer(state, cb) {
        state.timer = setInterval(cb, 1000);
      },
      cleanTimer(state) {
        if (state.timer) {
          clearInterval(state.timer);
          state.timer = null;
        }
      },
      reset(state) {
        state.currentQuestionIdx = 0;
        state.answers = {};
        state.reviewQuestions = false;
        state.now = new Date();
        if (state.timer) {
          clearInterval(state.timer);
          state.timer = null;
        }
        state.questionsTimeSpent = { };
      },
      setExamProcessing(state, condition) {
        state.examProcessing = condition;
      },
    },
    actions: {
      async fetchPreview({ commit, state }, id) {
        if (state.examProcessing) {
          return false;
        }

        try {
          commit('setExamProcessing', id);
          const exam = await this.$axios.$get(`/exams/${id}`, { progress: false, noError: true });
          commit('setExamProcessing', false);
          commit('setExam', { ...exam, questionsCount: exam.questions, casesCount: exam.cases });
        } catch (e) {
          commit('setExamProcessing', false);
          return false;
        }

        return true;
      },

      async fetchQuestions({ commit, state }, { id, mode }) {
        if (state.examProcessing) {
          return false;
        }

        try {
          commit('setExamProcessing', id);
          const exam = await this.$axios.$get(`/exams/${id}/questions?mode=${mode}`, { progress: false, noError: true });
          commit('setExamProcessing', false);
          commit('setExam', exam);
        } catch (e) {
          return false;
        }

        return true;
      },

      async start({ commit, state, rootState }, redirect = true) {
        if (state.examProcessing) {
          return false;
        }
        try {
          const now = Date.now();
          commit('setNow', now);
          if (rootState.auth.user) {
            commit('setExamProcessing', state.exam.id);
            const { userExamId } = await this.$axios.$post(`/exams/${state.exam.id}/started`, { timestamp: Math.floor(now / 1000) }, { progress: false });

            if (this.$config.project === 'site') {
              window.location.href = `${this.$config.lmsUrl}/exams/${userExamId}/`;
            } else {
              await this.$tracking.analytics().track('exam_started', {
                exam_id: state.exam.id,
                exam_type: state.exam.type,
                course_id: state.exam.courseId,
                track_id: state.exam.trackId,
                exam_attempt: state.exam.attempts + 1,
                exam_questions: state.exam.questionsCount,
                exam_duration: state.exam.duration,
              });
              if (redirect) {
                this.$router.push(`/exams/${userExamId}/`);
              }
              commit('setExamProcessing', false);
              return userExamId;
            }
          } else {
            this.$router.push(`/resources-center/exams/${state.exam.id}/`);
          }
        } catch (e) {
          console.log(e);
          commit('setExamProcessing', false);
        }
        return false;
      },

      async startExamTimer({ state, commit, getters }) {
        if (state.timer || state.exam.completed) return;

        commit('setNow');
        const cb = () => {
          commit('setNow');
          if (this.$router.currentRoute.name === 'exams-id' || this.$router.currentRoute.name === 'resources-center-exams-id') {
            commit('setTimeSpent', { question: getters.currentQuestion.id });
          }
        };

        commit('setTimer', cb);
      },

      setQuestion({ state, commit }, data) {
        const { i, leftDirection } = data;
        commit('setLeftDirection', leftDirection);

        let nextIdx = state.currentQuestionIdx + i;

        if (nextIdx < 0) {
          nextIdx = 0;
        } else if (nextIdx >= state.exam.questions.length) {
          nextIdx = state.exam.questions.length - 1;
        }

        return commit('setCurrentQuestionIdx', nextIdx);
      },

      async answerQuestion({
        state, getters, commit, rootState,
      }, { answer, mode }) {
        if (state.exam.completed) {
          return;
        }

        const question = getters.currentQuestion.id;

        commit('setAnswer', { question, answer });

        if (rootState.auth.user && !mode) {
          await this.$axios.$post(`/exams/${this.$router.currentRoute.params.id}/${question}/answer`, { answerId: answer }, { progress: false }).catch(() => false);
        } else {
          commit('setAnswerCreated', { question });
          window.sessionStorage.setItem('exam', JSON.stringify(state));
        }
      },

      async flagQuestion({
        getters, state, commit, rootState,
      }) {
        if (state.exam.completed) {
          return;
        }

        const question = getters.currentQuestion.id;

        const flag = !state.answers[question].flag;

        commit('setFlag', { question, flag });

        if (rootState.auth.user) {
          await this.$axios.$post(`/exams/${this.$router.currentRoute.params.id}/${question}/flag`, { flag }, { progress: false }).catch(() => false);
        } else {
          commit('setFlagCreated', { question });
          window.sessionStorage.setItem('exam', JSON.stringify(state));
        }
      },

      reviewQuestions({ commit, state, rootState }, s) {
        const panel = document.querySelector('.review-questions-panel');
        const expandBtn = document.querySelector('.review');
        const expandBtnArrow = expandBtn.querySelector('svg');

        const reviewQuestions = typeof s !== 'undefined' ? !s : state.reviewQuestions;

        if (reviewQuestions) {
          expandBtn.style.right = '0px';
          panel.style.right = '-412px';
          expandBtnArrow.style.transform = 'rotate(180deg)';
        } else {
          panel.style.right = 0;
          expandBtn.style.right = rootState.laptop ? '133px' : '120px';
          expandBtnArrow.style.transform = 'rotate(0)';
        }

        setTimeout(() => commit('reviewQuestions', s), 300);
      },

      async submit({
        state, dispatch, commit, rootState,
      }, { redirect = true } = {}) {
        if (state.exam.completed) {
          return false;
        }

        try {
          commit('setExamCompleted');

          if (rootState.auth.user) {
            const answers = Object.entries(state.answers).reduce((p, [k, v]) => ({ ...p, ...(v.answer ? { [k]: v.answer } : {}) }), {});
            const examResult = await this.$axios.$post(`/exams/${this.$router.currentRoute.params.id}/completed`, { answers }, { progress: false });

            await this.$tracking.analytics().track('exam_submitted', {
              exam_id: state.exam.id,
              exam_type: state.exam.type,
              course_id: state.exam.courseId,
              track_id: state.exam.trackId,
              exam_attempt: state.exam.attempts + 1,
              exam_questions: state.exam.questions.length,
              exam_duration: state.exam.timeout,
              correct_questions: examResult.correctAnswers,
              incorrect_questions: examResult.incorrectAnswers,
              empty_questions: examResult.notAnsweredQuestions,
              exam_score: examResult.score,
              exam_passed: false,
              exam_completion_time: examResult.completionTime,
              certificate_id: '',
            });
          } else {
            window.sessionStorage.setItem('exam', JSON.stringify(state));
          }

          if (redirect) {
            await dispatch('redirectToResults');
          }
        } catch (e) {
          console.log('problem submit');
        }

        return false;
      },

      redirectToResults({ state, rootState }) {
        if (state.exam.type === 'practice') {
          if (rootState.auth.user) {
            return this.$router.push(`/exams/practice/${state.exam.id}/results/`);
          }
          return this.$router.push('/resources-center/exams/signup/');
        }

        if (state.exam.type === 'course') {
          return this.$router.push(`/exams/course/${state.exam.courseId}/results/?redirected=1`);
        }

        return this.$router.push(`/exams/track/${state.exam.trackId}/results/${state.exam.version === 2 ? '?tab=attempt2&redirected=1' : '?redirected=1'}`);
      },

      async fetchPracticeExams(s, { q, page, filter }) {
        const { exams, count } = await this.$axios.$get(`/exams/practice?page=${page}&q=${q || ''}&${filter || ''}`, { progress: false });

        return { exams, count };
      },

      async fetchCourseExams(s, {
        q, page, filter, reward,
      }) {
        const data = await this.$axios.$get(`/exams/course?page=${page}&q=${q || ''}&${filter || ''}&reward=${+reward || ''}`, { progress: false });

        return data;
      },

      async fetchTrackExams() {
        const exams = await this.$axios.$get('/exams/track', { progress: false });

        return exams;
      },

      async spentTime({ dispatch }, oldQuestionIdx) {
        dispatch('sendSpentTime', oldQuestionIdx);
      },

      async sendSpentTime({ state, commit, rootState }, questionIdx) {
        if (state.exam.completed) return;
        if (!this.$router.currentRoute.path.includes('/exams/')) return;

        const question = state.exam.questions[questionIdx];
        const { time } = state.answers[question.id];

        if (rootState.auth.user) {
          // Reset time
          commit('setTimeSpent', { question: question.id, time: 0 });

          // Send save request
          await this.$axios.$post(`/exams/${this.$router.currentRoute.params.id}/${question.id}/spent-time`, { time }, { progress: false });
        } else {
          window.sessionStorage.setItem('exam', JSON.stringify(state));
        }
      },
    },
  });

  if (process.client) {
    store.watch(
      ({ exams }) => exams.currentQuestionIdx, async (currentQuestionIdx, oldQuestionIdx) => {
        await store.dispatch('exams/spentTime', oldQuestionIdx);
      },
      { immediate: false },
    );
  }
};
